import React, { useMemo, useState } from 'react';
import { 
  Equipment, 
  InspectionPlan, 
  InspectionActual, 
  MonitoringRow, 
  InspectionStatus,
  Finding,
  AppUser,
  EquipmentType,
  PlanStatus
} from '../types';
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Calendar,
  Search,
  Eye,
  X,
  MapPin,
  Camera,
  Check,
  AlertTriangle,
  HelpCircle,
  Users,
  Box,
  Target,
  RefreshCw,
  LayoutList,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  ArrowUpRight
} from 'lucide-react';
import { EQUIPMENT_ICONS } from '../constants';

interface MonitoringProps {
  equipments: Equipment[];
  plans: InspectionPlan[];
  actuals: InspectionActual[];
  findings: Finding[];
  user: AppUser;
}

const Monitoring: React.FC<MonitoringProps> = ({ equipments, plans, actuals, findings, user }) => {
  // Filter States
  const [filterMonth, setFilterMonth] = useState<number>(new Date().getMonth());
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterArea, setFilterArea] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  
  const [selectedResult, setSelectedResult] = useState<{ plan: InspectionPlan, actual: InspectionActual, equipment: Equipment } | null>(null);

  const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 10 }, (_, i) => currentYear - 5 + i), [currentYear]);
  const assetTypes = Object.values(EquipmentType);
  const areas = useMemo(() => Array.from(new Set(equipments.map(e => e.area))), [equipments]);

  const resetFilters = () => {
    setFilterMonth(new Date().getMonth());
    setFilterYear(new Date().getFullYear());
    setFilterType('ALL');
    setFilterArea('ALL');
    setSearchTerm('');
  };

  const dayNames = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

  // Data processing for Monitoring View
  const monitoringData = useMemo(() => {
    return plans
      .map(plan => {
        const equipment = equipments.find(e => e.id === plan.equipmentId);
        if (!equipment) return null;

        const planDate = new Date(plan.plannedDate);
        const matchMonth = planDate.getMonth() === filterMonth;
        const matchYear = planDate.getFullYear() === filterYear;
        const matchType = filterType === 'ALL' || equipment.type === filterType;
        const matchArea = filterArea === 'ALL' || equipment.area === filterArea;

        if (!matchMonth || !matchYear || !matchType || !matchArea) return null;

        const actual = actuals.find(a => 
          a.planId === plan.id || 
          (a.equipmentId === plan.equipmentId && a.actualDate === plan.plannedDate)
        );
        
        // Revised Logic based on User Truth Table:
        // 1. IF actual_date IS NULL and due_date >= TODAY -> Scheduled
        // 2. IF actual_date IS NULL and due_date < TODAY -> Overdue
        // 3. IF actual_date <= due_date -> On Time
        // 4. IF actual_date > due_date -> Late
        let complianceStatus: MonitoringRow['complianceStatus'] = 'Pending';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const planDateObj = new Date(plan.plannedDate);
        planDateObj.setHours(0, 0, 0, 0);

        if (actual) {
          const actualDateObj = new Date(actual.actualDate);
          actualDateObj.setHours(0, 0, 0, 0);
          complianceStatus = actualDateObj <= planDateObj ? 'On Time' : 'Late';
        } else {
          // No actual date yet
          if (today > planDateObj) {
            complianceStatus = 'Missed'; // Displayed as Overdue
          } else {
            complianceStatus = 'Pending'; // Displayed as Scheduled
          }
        }

        return { plan, actual, equipment, complianceStatus } as MonitoringRow;
      })
      .filter((row): row is MonitoringRow => {
        if (!row) return false;
        const searchStr = `${row.equipment.code} ${row.equipment.area} ${row.plan.pic} ${row.equipment.type}`.toLowerCase();
        return searchStr.includes(searchTerm.toLowerCase());
      });
  }, [equipments, plans, actuals, filterMonth, filterYear, filterType, filterArea, searchTerm]);

  // Calendar Logic
  const calendarDays = useMemo(() => {
    const firstDay = new Date(filterYear, filterMonth, 1).getDay();
    const daysInMonth = new Date(filterYear, filterMonth + 1, 0).getDate();
    
    const days = [];
    // Previous month padding
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, date: null });
    }
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
        const dateStr = `${filterYear}-${String(filterMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
        const dayPlans = monitoringData.filter(d => d.plan.plannedDate === dateStr);
        days.push({ day: i, date: dateStr, data: dayPlans });
    }
    return days;
  }, [filterMonth, filterYear, monitoringData]);

  // Stats for current filtered period
  const stats = useMemo(() => {
    const total = monitoringData.length;
    const completed = monitoringData.filter(d => d.complianceStatus === 'On Time' || d.complianceStatus === 'Late').length;
    const onTime = monitoringData.filter(d => d.complianceStatus === 'On Time').length;
    const late = monitoringData.filter(d => d.complianceStatus === 'Late').length;
    const overdue = monitoringData.filter(d => d.complianceStatus === 'Missed').length;
    
    return {
        total,
        completed,
        overdue,
        late,
        rate: total > 0 ? Math.round((completed / total) * 100) : 100,
        onTimeRate: completed > 0 ? Math.round((onTime / completed) * 100) : 0
    };
  }, [monitoringData]);

  const handlePrevMonth = () => {
    if (filterMonth === 0) {
      setFilterMonth(11);
      setFilterYear(y => y - 1);
    } else {
      setFilterMonth(m => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (filterMonth === 11) {
      setFilterMonth(0);
      setFilterYear(y => y + 1);
    } else {
      setFilterMonth(m => m + 1);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Audit Monitoring Hub</h1>
          <p className="text-slate-500 mt-1 font-medium italic">Dashboard visual kepatuhan inspeksi dan validasi kesiapan audit.</p>
        </div>
        <div className="flex gap-3">
            <div className="bg-white p-1 rounded-2xl border border-slate-200 flex shadow-sm">
                <button 
                    onClick={() => setViewMode('calendar')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'calendar' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <CalendarDays className="w-4 h-4" /> Calendar View
                </button>
                <button 
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'list' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <LayoutList className="w-4 h-4" /> List View
                </button>
            </div>
            <button 
                onClick={resetFilters}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-rose-500 hover:border-rose-200 transition-all shadow-sm"
            >
                <RefreshCw className="w-4 h-4" /> Reset
            </button>
        </div>
      </div>

      {/* Advanced Filter Console */}
      <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative group">
            <label className="absolute -top-2 left-4 bg-white px-2 text-[9px] font-black uppercase text-slate-400 tracking-widest z-10">Period Month</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <select 
                value={filterMonth} 
                onChange={(e) => setFilterMonth(parseInt(e.target.value))}
                className="w-full pl-11 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-black uppercase tracking-tight outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all appearance-none cursor-pointer"
              >
                {monthNames.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="relative group">
            <label className="absolute -top-2 left-4 bg-white px-2 text-[9px] font-black uppercase text-slate-400 tracking-widest z-10">Period Year</label>
            <div className="relative">
              <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <select 
                value={filterYear} 
                onChange={(e) => setFilterYear(parseInt(e.target.value))}
                className="w-full pl-11 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-black uppercase tracking-tight outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all appearance-none cursor-pointer"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="relative group">
            <label className="absolute -top-2 left-4 bg-white px-2 text-[9px] font-black uppercase text-slate-400 tracking-widest z-10">Asset Type</label>
            <div className="relative">
              <Box className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pl-11 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-black uppercase tracking-tight outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all appearance-none cursor-pointer"
              >
                <option value="ALL">All Categories</option>
                {assetTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="relative group">
            <label className="absolute -top-2 left-4 bg-white px-2 text-[9px] font-black uppercase text-slate-400 tracking-widest z-10">Operational Area</label>
            <div className="relative">
              <Target className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <select 
                value={filterArea} 
                onChange={(e) => setFilterArea(e.target.value)}
                className="w-full pl-11 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-black uppercase tracking-tight outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all appearance-none cursor-pointer"
              >
                <option value="ALL">All Areas</option>
                {areas.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
      </div>

      {viewMode === 'calendar' ? (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            <div className="xl:col-span-8 bg-white p-6 md:p-10 rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-500">
                            <CalendarDays className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none">{monthNames[filterMonth]} {filterYear}</h2>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2 px-1">Planned Compliance Grid</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handlePrevMonth} className="p-3 hover:bg-slate-100 rounded-xl transition-all"><ChevronLeft className="w-5 h-5" /></button>
                        <button onClick={handleNextMonth} className="p-3 hover:bg-slate-100 rounded-xl transition-all"><ChevronRight className="w-5 h-5" /></button>
                    </div>
                </div>

                <div className="grid grid-cols-7 gap-px bg-slate-100 border border-slate-100 rounded-[2.5rem] overflow-hidden shadow-sm">
                    {dayNames.map(d => (
                        <div key={d} className="bg-slate-50/80 p-4 text-center">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{d}</span>
                        </div>
                    ))}
                    {calendarDays.map((day, idx) => (
                        <div key={idx} className={`bg-white min-h-[140px] p-3 flex flex-col gap-2 relative transition-all ${day.day ? 'hover:bg-slate-50' : 'opacity-20'}`}>
                            {day.day && (
                                <>
                                    <span className={`text-[11px] font-black w-6 h-6 flex items-center justify-center rounded-lg ${new Date(day.date!).toDateString() === new Date().toDateString() ? 'text-white bg-emerald-600 shadow-lg shadow-emerald-600/20' : 'text-slate-300'}`}>
                                        {day.day}
                                    </span>
                                    <div className="space-y-1 overflow-y-auto max-h-[100px] scrollbar-none pr-1">
                                        {day.data?.map(row => {
                                            const statusColor = row.complianceStatus === 'On Time' ? 'bg-emerald-500' : 
                                                              row.complianceStatus === 'Late' ? 'bg-amber-500' : 
                                                              row.complianceStatus === 'Missed' ? 'bg-rose-500' : 'bg-slate-300';
                                            return (
                                                <div 
                                                    key={row.plan.id}
                                                    onClick={() => row.actual && setSelectedResult({ plan: row.plan, actual: row.actual!, equipment: row.equipment })}
                                                    className={`px-2 py-1.5 rounded-[10px] text-[8px] font-black uppercase tracking-tighter flex items-center gap-1.5 border border-black/5 cursor-pointer hover:shadow-md hover:scale-105 transition-all ${row.actual ? 'bg-white shadow-sm border-slate-100' : 'bg-slate-50/50'}`}
                                                >
                                                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusColor}`}></div>
                                                    <span className={`truncate flex-1 ${row.complianceStatus === 'Missed' ? 'text-rose-600' : 'text-slate-600'}`}>{row.equipment.code}</span>
                                                    {row.actual?.status === 'NG' && <ShieldAlert className="w-2.5 h-2.5 text-rose-500 shrink-0" />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="xl:col-span-4 flex flex-col gap-8">
                <div className="bg-slate-950 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                        <ShieldCheck className="w-48 h-48 text-emerald-500" />
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-lg font-black uppercase tracking-tighter mb-8 flex items-center gap-3">
                            <ShieldCheck className="w-6 h-6 text-emerald-400" /> Month Audit Readiness
                        </h3>
                        <div className="space-y-10">
                            <div className="flex items-end justify-between">
                                <div>
                                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Compliance</p>
                                    <h4 className="text-5xl font-black tracking-tighter">{stats.rate}%</h4>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">On-Time Execution</p>
                                    <h4 className="text-2xl font-black text-emerald-400 tracking-tighter">{stats.onTimeRate}%</h4>
                                </div>
                            </div>
                            <div className="h-4 bg-white/5 rounded-full overflow-hidden p-1 border border-white/10">
                                <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(16,185,129,0.3)]" style={{ width: `${stats.rate}%` }}></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-5 bg-white/5 rounded-3xl border border-white/10">
                                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Target Tasks</p>
                                    <p className="text-2xl font-black">{stats.total}</p>
                                </div>
                                <div className="p-5 bg-white/5 rounded-3xl border border-white/10">
                                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Overdue Tasks</p>
                                    <p className="text-2xl font-black text-rose-500">{stats.overdue}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm flex-1">
                    <h3 className="text-base font-black text-slate-900 uppercase tracking-tighter mb-8 flex items-center gap-3">
                        <ShieldAlert className="w-6 h-6 text-rose-500" /> Pending Findings
                    </h3>
                    <div className="space-y-4">
                        {monitoringData.filter(d => d.complianceStatus === 'Missed' || d.actual?.status === 'NG').length > 0 ? (
                            monitoringData.filter(d => d.complianceStatus === 'Missed' || d.actual?.status === 'NG').slice(0, 5).map(row => (
                                <div key={row.plan.id} className="p-5 bg-slate-50 border border-slate-100 rounded-3xl flex items-center justify-between group hover:shadow-md transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white ${row.complianceStatus === 'Missed' ? 'bg-rose-500' : 'bg-orange-500 shadow-orange-500/30 shadow-lg'}`}>
                                            {row.complianceStatus === 'Missed' ? <Clock className="w-5 h-5 shadow-inner" /> : <ShieldAlert className="w-5 h-5 shadow-inner" />}
                                        </div>
                                        <div>
                                            <p className="text-xs font-black text-slate-900 tracking-tight">{row.equipment.code}</p>
                                            <div className="flex items-center gap-2">
                                                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{row.plan.plannedDate}</p>
                                                <span className={`px-1.5 py-0.5 rounded text-[7px] font-black uppercase ${row.complianceStatus === 'Missed' ? 'bg-rose-100 text-rose-600' : 'bg-orange-100 text-orange-600'}`}>
                                                    {row.complianceStatus === 'Missed' ? 'OVERDUE' : 'NG ALERT'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-900 transition-all" />
                                </div>
                            ))
                        ) : (
                            <div className="py-16 text-center opacity-30 flex flex-col items-center gap-3">
                                <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                                <p className="text-[10px] font-black uppercase tracking-widest">No Critical Alerts</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
      ) : (
        <div className="bg-white rounded-[3rem] border border-gray-100 shadow-sm overflow-hidden animate-in slide-in-from-bottom-4 duration-500">
            <div className="p-8 border-b border-gray-100 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex-1 relative">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input 
                        type="text" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search Code, Section, or PIC..." 
                        className="w-full pl-16 pr-8 py-5 bg-white border border-gray-200 rounded-[2rem] focus:ring-4 focus:ring-emerald-500/10 text-sm font-bold outline-none transition-all shadow-sm"
                    />
                </div>
                <div className="px-8 py-5 bg-white border border-slate-100 rounded-2xl shadow-sm text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {monitoringData.length} Results Found
                </div>
            </div>
            
            <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-slate-50/80">
                    <tr>
                        <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-gray-100">Asset Info</th>
                        <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-gray-100 text-center">Plan Date</th>
                        <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-gray-100 text-center">Actual Date</th>
                        <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Status</th>
                        <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">View</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {monitoringData.length > 0 ? (
                    monitoringData.map((row) => (
                        <tr key={row.plan.id} className="hover:bg-slate-50/50 transition-colors group">
                            <td className="px-8 py-8 border-r border-gray-100">
                                <div className="flex items-center gap-5">
                                    <div className="w-12 h-12 rounded-[1.25rem] flex items-center justify-center bg-slate-100 text-slate-500 group-hover:bg-white group-hover:shadow-md transition-all">
                                        {EQUIPMENT_ICONS[row.equipment.type]}
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-800 text-sm tracking-tight">{row.equipment.code}</p>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{row.equipment.type} • {row.equipment.area}</p>
                                    </div>
                                </div>
                            </td>
                            <td className="px-8 py-8 border-r border-gray-100 text-center">
                                <div className="inline-flex items-center gap-2 font-bold text-slate-700 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
                                    <Calendar className="w-4 h-4 text-blue-500" />
                                    {row.plan.plannedDate}
                                </div>
                                <p className="text-[9px] text-slate-400 font-bold mt-2 uppercase tracking-wide">PIC: {row.plan.pic}</p>
                            </td>
                            <td className="px-8 py-8 border-r border-gray-100 text-center">
                                {row.actual ? (
                                    <>
                                        <div className="inline-flex items-center gap-2 font-bold text-slate-700 bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                            {row.actual.actualDate}
                                        </div>
                                        <p className="text-[9px] text-slate-400 font-bold mt-2 uppercase tracking-wide italic">Verified by {row.actual.inspector}</p>
                                    </>
                                ) : (
                                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest italic animate-pulse">Waiting for execution...</span>
                                )}
                            </td>
                            <td className="px-8 py-8 text-center uppercase">
                                <div className="flex flex-col items-center gap-2">
                                    <StatusBadge status={row.complianceStatus} />
                                    {row.actual?.status === InspectionStatus.NG && (
                                        <span className="px-3 py-1 bg-rose-600 text-white rounded-lg text-[8px] font-black shadow-lg shadow-rose-600/20">NG ALERT</span>
                                    )}
                                </div>
                            </td>
                            <td className="px-8 py-8 text-right">
                                {row.actual && (
                                    <button 
                                    onClick={() => setSelectedResult({ plan: row.plan, actual: row.actual!, equipment: row.equipment })}
                                    className="p-4 text-slate-400 hover:text-emerald-600 hover:bg-white rounded-2xl transition-all shadow-sm hover:shadow-xl border border-transparent hover:border-emerald-100"
                                    >
                                        <Eye className="w-6 h-6" />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))
                    ) : (
                    <tr>
                        <td colSpan={5} className="py-32 text-center">
                            <div className="opacity-20 flex flex-col items-center gap-6">
                                <Search className="w-16 h-16" />
                                <p className="italic font-black uppercase text-sm tracking-widest">No historical data identified</p>
                            </div>
                        </td>
                    </tr>
                    )}
                </tbody>
            </table>
            </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {selectedResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-2xl animate-in fade-in duration-300">
              <div className="bg-white rounded-[4rem] w-full max-w-5xl shadow-2xl overflow-hidden scale-in border border-white/20">
                  <div className="p-10 border-b border-slate-100 flex justify-between items-start bg-slate-50/50">
                      <div className="flex items-center gap-8">
                        <div className="w-20 h-20 bg-white rounded-3xl shadow-xl flex items-center justify-center text-emerald-500 border border-slate-100">
                            {EQUIPMENT_ICONS[selectedResult.equipment.type]}
                        </div>
                        <div>
                            <div className="flex items-center gap-4 mb-2">
                                <span className="px-4 py-1.5 bg-slate-900 text-white rounded-xl text-[11px] font-black uppercase tracking-widest">{selectedResult.equipment.code}</span>
                                <StatusBadge status={new Date(selectedResult.actual.actualDate) <= new Date(selectedResult.plan.plannedDate) ? 'On Time' : 'Late'} />
                            </div>
                            <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Inspection Audit Record</h3>
                            <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mt-2 flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5 text-rose-500" /> {selectedResult.equipment.area} • {selectedResult.equipment.section}
                            </p>
                        </div>
                      </div>
                      <button onClick={() => setSelectedResult(null)} className="p-5 text-slate-300 hover:text-rose-500 bg-white rounded-2xl shadow-xl transition-all border border-slate-100"><X className="w-10 h-10" /></button>
                  </div>

                  <div className="p-12 max-h-[65vh] overflow-y-auto custom-scrollbar">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        <div className="space-y-12">
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner text-center">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Assigned PIC</p>
                                    <p className="font-bold text-slate-900 flex items-center justify-center gap-3"><Users className="w-5 h-5 text-indigo-500" /> {selectedResult.plan.pic}</p>
                                </div>
                                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner text-center">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Actual Inspector</p>
                                    <p className="font-bold text-slate-900 flex items-center justify-center gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> {selectedResult.actual.inspector}</p>
                                </div>
                                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner text-center">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Technical Status</p>
                                    <p className={`font-black uppercase text-lg ${selectedResult.actual.status === 'OK' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {selectedResult.actual.status} UNIT
                                    </p>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-[11px] font-black text-slate-950 uppercase tracking-[0.2em] mb-6 flex items-center gap-3">
                                    <Check className="w-5 h-5 text-emerald-500" /> Structural Checklist
                                </h4>
                                <div className="grid grid-cols-1 gap-3">
                                    {selectedResult.actual.checkItems ? Object.entries(selectedResult.actual.checkItems).map(([item, ok]) => (
                                        <div key={item} className="flex items-center justify-between p-5 bg-white border border-slate-100 rounded-2xl hover:border-emerald-100 transition-colors shadow-sm">
                                            <span className="text-[11px] font-black text-slate-600 uppercase tracking-tight">{item}</span>
                                            {ok ? (
                                                <div className="bg-emerald-500 text-white p-2 rounded-xl shadow-lg shadow-emerald-500/20"><Check className="w-4 h-4" /></div>
                                            ) : (
                                                <div className="bg-rose-500 text-white p-2 rounded-xl shadow-lg shadow-rose-500/20"><AlertTriangle className="w-4 h-4" /></div>
                                            )}
                                        </div>
                                    )) : <p className="text-slate-400 italic text-sm text-center py-6">Checklist detail tidak tersedia.</p>}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-12">
                            <div>
                                <h4 className="text-[11px] font-black text-slate-950 uppercase tracking-[0.2em] mb-6 flex items-center gap-3">
                                    <Camera className="w-5 h-5 text-indigo-500" /> Visual Verification
                                </h4>
                                {selectedResult.actual.evidencePhoto ? (
                                    <div className="rounded-[3rem] overflow-hidden border-[12px] border-slate-50 shadow-2xl bg-slate-100 aspect-video group relative">
                                        <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                            <p className="text-[10px] font-black text-white uppercase tracking-widest text-center">Recorded via Mobile Interface</p>
                                        </div>
                                        <img src={selectedResult.actual.evidencePhoto} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" alt="Evidence" />
                                    </div>
                                ) : (
                                    <div className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-[3rem] flex flex-col items-center justify-center text-slate-200">
                                        <Camera className="w-16 h-16 mb-4" />
                                        <p className="text-[11px] font-black uppercase tracking-widest">Metadata Only - No Visuals</p>
                                    </div>
                                )}
                            </div>
                            
                            <div className="p-8 bg-slate-950 rounded-[2.5rem] border border-white/5 relative overflow-hidden group shadow-2xl">
                                <div className="absolute -top-10 -right-10 opacity-5 group-hover:rotate-12 transition-transform duration-1000">
                                    <Box className="w-32 h-32 text-emerald-500" />
                                </div>
                                <p className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em] mb-4 text-center">Internal Field Notes</p>
                                <p className="text-base text-slate-300 font-medium italic relative z-10 leading-relaxed tracking-tight group-hover:text-white transition-colors text-center pb-2">"{selectedResult.actual.notes || 'No additional logs provided by inspector.'}"</p>
                            </div>
                        </div>
                    </div>
                  </div>

                  <div className="p-10 bg-slate-50/50 border-t border-slate-100 flex justify-end">
                      <button onClick={() => setSelectedResult(null)} className="px-12 py-5 bg-slate-900 text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-xl hover:bg-emerald-600 transition-all active:scale-95">
                          Acknowledge & Close
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }: { status: MonitoringRow['complianceStatus'] }) => {
    const config: Record<string, string> = {
        'On Time': 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20',
        'Late': 'bg-amber-500 text-white shadow-lg shadow-amber-500/20',
        'Missed': 'bg-rose-500 text-white shadow-lg shadow-rose-500/20',
        'Pending': 'bg-slate-200 text-slate-600',
    };

    const label: Record<string, string> = {
        'On Time': 'On Time',
        'Late': 'Late',
        'Missed': 'Overdue',
        'Pending': 'Scheduled',
    };

    return (
        <span className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest ${config[status]}`}>
            {label[status]}
        </span>
    );
};

export default Monitoring;
